/**
 * Shared hub state for Plan Payment Order (sframe-202 → dt-2) and child checklist routes.
 */

export const PLAN_PAYMENT_HUB_FRAME_ID = 'sframe-202';

export const PLAN_PAYMENT_SECTION_KEYS = ['s1', 's2', 's3', 's4', 's5'] as const;
export type PlanPaymentHubSectionKey = (typeof PLAN_PAYMENT_SECTION_KEYS)[number];

export interface PlanPaymentChildSpec {
  hubKey: PlanPaymentHubSectionKey;
  frameId: string;
  itemIds: readonly string[];
}

export const PLAN_PAYMENT_CHILD_SPECS: PlanPaymentChildSpec[] = [
  {
    hubKey: 's1',
    frameId: 'sframe-202-s1',
    itemIds: ['pp-s1-statutes', 'pp-s1-secured-unsecured', 'pp-s1-liability'],
  },
  {
    hubKey: 's2',
    frameId: 'sframe-202-s2',
    itemIds: ['pp-s2-admin', 'pp-s2-tax', 'pp-s2-general'],
  },
  {
    hubKey: 's3',
    frameId: 'sframe-202-s3',
    itemIds: ['pp-s3-liquid', 'pp-s3-compare', 'pp-s3-sell'],
  },
  {
    hubKey: 's4',
    frameId: 'sframe-202-s4',
    itemIds: ['pp-s4-wait', 'pp-s4-sequence', 'pp-s4-attorney'],
  },
  {
    hubKey: 's5',
    frameId: 'sframe-202-s5',
    itemIds: ['pp-s5-rankings', 'pp-s5-report', 'pp-s5-approve'],
  },
];

export const PLAN_PAYMENT_SPEC_LEGAL = PLAN_PAYMENT_CHILD_SPECS[0]!;
export const PLAN_PAYMENT_SPEC_DEBT_PRIORITY = PLAN_PAYMENT_CHILD_SPECS[1]!;
export const PLAN_PAYMENT_SPEC_LIQUIDITY = PLAN_PAYMENT_CHILD_SPECS[2]!;
export const PLAN_PAYMENT_SPEC_SCHEDULE = PLAN_PAYMENT_CHILD_SPECS[3]!;
export const PLAN_PAYMENT_SPEC_FINALIZE = PLAN_PAYMENT_CHILD_SPECS[4]!;

export function defaultPlanPaymentHubSections(): Record<PlanPaymentHubSectionKey, boolean> {
  return { s1: false, s2: false, s3: false, s4: false, s5: false };
}

export function mergePlanPaymentHubSections(
  raw: Record<string, unknown> | undefined
): Record<PlanPaymentHubSectionKey, boolean> {
  const base = defaultPlanPaymentHubSections();
  const hub = raw?.hubSections as Partial<Record<PlanPaymentHubSectionKey, boolean>> | undefined;
  if (!hub || typeof hub !== 'object') return base;
  for (const k of PLAN_PAYMENT_SECTION_KEYS) {
    if (typeof hub[k] === 'boolean') base[k] = hub[k];
  }
  return base;
}

export function countPlanPaymentSectionsDone(
  sections: Record<PlanPaymentHubSectionKey, boolean>
): number {
  return PLAN_PAYMENT_SECTION_KEYS.filter((k) => sections[k]).length;
}

function allChecksTrue(rawChecks: Record<string, boolean> | undefined, ids: readonly string[]): boolean {
  if (!ids.length) return false;
  return ids.every((id) => !!rawChecks?.[id]);
}

export async function derivePlanPaymentSectionsFromChildren(
  loadFrameData: (
    frameId: string
  ) => Promise<{ data?: Record<string, unknown> } | null | undefined>
): Promise<Record<PlanPaymentHubSectionKey, boolean>> {
  const next = defaultPlanPaymentHubSections();
  for (const spec of PLAN_PAYMENT_CHILD_SPECS) {
    const framed = await loadFrameData(spec.frameId);
    const rawChecks = framed?.data?.checks as Record<string, boolean> | undefined;
    next[spec.hubKey] = allChecksTrue(rawChecks, spec.itemIds);
  }
  return next;
}

export async function savePlanPaymentHubSectionsAndSyncDt2(
  loadFrameData: (frameId: string) => Promise<{ data?: Record<string, unknown> } | null>,
  saveFrameData: (frameId: string, data: Record<string, unknown>) => Promise<boolean>,
  updateStepProgress: (
    stepId: string,
    phase: string,
    updates: { status: 'not-started' | 'in-progress' | 'complete' }
  ) => Promise<boolean>,
  nextSections: Record<PlanPaymentHubSectionKey, boolean>
): Promise<boolean> {
  const existing = await loadFrameData(PLAN_PAYMENT_HUB_FRAME_ID);
  const mergedData: Record<string, unknown> = { ...(existing?.data || {}) };
  mergedData.hubSections = nextSections;
  const ok = await saveFrameData(PLAN_PAYMENT_HUB_FRAME_ID, mergedData);
  const doneCount = countPlanPaymentSectionsDone(nextSections);
  await updateStepProgress('dt-2', 'debts-taxes', {
    status:
      doneCount >= PLAN_PAYMENT_SECTION_KEYS.length
        ? 'complete'
        : doneCount > 0
          ? 'in-progress'
          : 'not-started',
  });
  return ok;
}

/** Reconcile hub booleans from child checklist data and persist + sync dt-2 progress. */
export async function reconcilePlanPaymentHubFromChildren(
  loadFrameData: (frameId: string) => Promise<{ data?: Record<string, unknown> } | null>,
  saveFrameData: (frameId: string, data: Record<string, unknown>) => Promise<boolean>,
  updateStepProgress: (
    stepId: string,
    phase: string,
    updates: { status: 'not-started' | 'in-progress' | 'complete' }
  ) => Promise<boolean>
): Promise<Record<PlanPaymentHubSectionKey, boolean>> {
  const derived = await derivePlanPaymentSectionsFromChildren(loadFrameData);
  await savePlanPaymentHubSectionsAndSyncDt2(
    loadFrameData,
    saveFrameData,
    updateStepProgress,
    derived
  );
  return derived;
}

/** After toggling checklist items on a child page: update that section flag from checks and persist. */
export async function updatePlanPaymentSectionFromChecks(
  loadFrameData: (frameId: string) => Promise<{ data?: Record<string, unknown> } | null>,
  saveFrameData: (frameId: string, data: Record<string, unknown>) => Promise<boolean>,
  updateStepProgress: (
    stepId: string,
    phase: string,
    updates: { status: 'not-started' | 'in-progress' | 'complete' }
  ) => Promise<boolean>,
  spec: PlanPaymentChildSpec,
  checks: Record<string, boolean>
): Promise<void> {
  const sectionComplete = allChecksTrue(checks, spec.itemIds);
  const hubFramed = await loadFrameData(PLAN_PAYMENT_HUB_FRAME_ID);
  const merged = mergePlanPaymentHubSections(hubFramed?.data as Record<string, unknown> | undefined);
  merged[spec.hubKey] = sectionComplete;
  await savePlanPaymentHubSectionsAndSyncDt2(
    loadFrameData,
    saveFrameData,
    updateStepProgress,
    merged
  );
}
